// =============================================================================
// SESSION HANDOFF CONSUME — the FE half of the R188 session-holding fix.
//
// The OAuth callback runs top-level on the API WORKER's origin (Google's
// registered redirect_uri), so its Set-Cookie can only land there — useless
// to this app, which lives on its own Pages origin. For a non-default
// allowlisted return origin (us), the callback therefore appends a one-time
// ?session_handoff=CODE to the redirect. On boot, BEFORE the first /auth/me
// probe, this module trades that code for the session cookie via
// POST /auth/handoff — called through the app's own same-origin Pages proxy
// (public/_worker.js), so the relayed Set-Cookie lands FIRST-PARTY here and
// every subsequent same-origin /api//auth fetch carries it.
//
// The code is single-use, 60s-TTL, bound to one session, and never logged;
// the param is stripped from the URL (history.replaceState) whether the
// consume succeeded or not, so it never lingers in the address bar, history,
// or a copy-pasted link. A failed consume (expired/replayed code) simply
// leaves the user signed out — signing in again mints a fresh code.
// =============================================================================

import { apiFetch } from './api';

export const SESSION_HANDOFF_PARAM = 'session_handoff';

/**
 * If the current URL carries ?session_handoff=CODE: POST it to /auth/handoff
 * (setting the session cookie first-party on this origin), then strip the
 * param from the URL in place. Resolves true only when the handoff actually
 * set a session; false covers both "no param present" (the overwhelmingly
 * common boot) and "code rejected" (expired / already used / bad).
 *
 * Reads location/history via globalThis so the node-environment tests can
 * stub them (the same vi.stubGlobal discipline api.*.test.ts already uses
 * for fetch) — no jsdom dependency.
 */
export async function consumeSessionHandoff(): Promise<boolean> {
  const loc = globalThis.location;
  const hist = globalThis.history;
  if (!loc || !hist) return false;

  const url = new URL(loc.href);
  const code = url.searchParams.get(SESSION_HANDOFF_PARAM);
  if (!code) return false;

  try {
    await apiFetch<{ ok: boolean }>('/auth/handoff', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    return true;
  } catch {
    // Expired, replayed, or otherwise rejected — the user stays signed out
    // and can just sign in again. Never rethrown: boot must not crash over a
    // stale one-time code.
    return false;
  } finally {
    // Strip the param regardless of outcome (single-use server-side either
    // way — a URL still carrying it is misleading, not dangerous).
    url.searchParams.delete(SESSION_HANDOFF_PARAM);
    hist.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }
}
